import { Component } from '@theme/component';
import { ThemeEvents, NoLabelCheckboxChangeEvent } from '@theme/events';

/**
 * @typedef {object} FPDProductStateRefs
 * @property {HTMLButtonElement} [fpdButton] - FPD "Jetzt Etikett gestalten" Button
 * @property {HTMLButtonElement} [addToCartButton] - Add to Cart Button
 * @property {HTMLElement} [acceleratedCheckout] - Container für Payment Buttons
 * @property {HTMLInputElement} [noLabelCheckbox] - "Ohne Etikett" Checkbox
 */

/**
 * Ein Custom Element zur Verwaltung des Produkt-States für FPD-Integration.
 * Verwaltet die Zustände von:
 * - FPD Customize Button (nur aktiv wenn Variante vollständig gewählt)
 * - Add-to-Cart Button (nur aktiv wenn FPD-Design erstellt ODER "Ohne Etikett" aktiviert)
 * - Payment Buttons (siehe Add-to-Cart)
 * 
 * @extends {Component<FPDProductStateRefs>}
 */
export default class FPDProductStateComponent extends Component {
  /** @type {boolean} */
  #variantSelected = false;

  /** @type {boolean} */
  #fpdDesignCompleted = false;

  /** @type {boolean} */
  #noLabelChecked = false;

  /** @type {AbortController} */
  #abortController = new AbortController();

  connectedCallback() {
    super.connectedCallback();

    const { signal } = this.#abortController;

    // Prüfe Initial-State der Checkbox
    this.#checkInitialCheckboxState();

    // Prüfe ob eine Variante bereits ausgewählt ist
    this.#checkInitialVariantState();

    // Event Listener für Varianten-Auswahl
    document.addEventListener(ThemeEvents.variantSelected, this.#handleVariantSelected, { signal });
    document.addEventListener(ThemeEvents.variantUpdate, this.#handleVariantUpdate, { signal });

    // Event Listener für "Ohne Etikett" Checkbox
    document.addEventListener(ThemeEvents.noLabelCheckboxChange, this.#handleNoLabelCheckboxChange, { signal });
    
    // Direkt auf Checkbox-Änderungen hören als Fallback
    const checkbox = document.querySelector('input[name="properties[Ohne Etikett]"]');
    if (checkbox instanceof HTMLInputElement) {
      checkbox.addEventListener('change', this.#handleCheckboxDirectChange, { signal });
    }

    // Warte auf FPD.instance und registriere Event Listener
    this.#setupFPDEventListeners();

    // Initial Button States setzen (warte kurz damit DOM ready ist)
    setTimeout(() => {
      console.log('Initial State Check:', this.getState());
      this.#updateButtonStates();
    }, 100);
  }

  disconnectedCallback() {
    super.disconnectedCallback();
    this.#abortController.abort();
  }

  /**
   * Wartet auf FPD.instance und registriert Event Listener
   */
  #setupFPDEventListeners() {
    const setupListeners = () => {
      // @ts-ignore - FPD ist eine globale Variable
      const fpd = window.FPD;
      if (typeof fpd !== 'undefined' && fpd.instance) {
        console.log('FPD.instance gefunden, registriere Event Listener');
        
        fpd.instance.addEventListener('modalDesignerDone', this.#handleFPDDesignCompleted);
        fpd.instance.addEventListener('fpd:cartChanged', this.#handleFPDCartChanged);
        
        // Cleanup bei disconnect
        this.#abortController.signal.addEventListener('abort', () => {
          fpd.instance.removeEventListener('modalDesignerDone', this.#handleFPDDesignCompleted);
          fpd.instance.removeEventListener('fpd:cartChanged', this.#handleFPDCartChanged);
        });
      } else {
        // Versuche es in 100ms erneut
        setTimeout(setupListeners, 100);
      }
    };
    
    setupListeners();
  }

  /**
   * Prüft den initialen Zustand der Checkbox
   */
  #checkInitialCheckboxState() {
    const checkbox = document.querySelector('input[name="properties[Ohne Etikett]"]');
    if (checkbox instanceof HTMLInputElement) {
      this.#noLabelChecked = checkbox.checked;
    }
  }

  /**
   * Prüft ob bereits eine Variante ausgewählt ist (z.B. bei URL mit ?variant=ID)
   */
  #checkInitialVariantState() {
    // Prüfe ob variant-configurator existiert und eine Variante ausgewählt ist
    const configurator = document.querySelector('variant-configurator-component');
    if (configurator && typeof configurator === 'object' && 'selectedVariant' in configurator) {
      this.#variantSelected = configurator.selectedVariant !== null;
      console.log('Variant Configurator gefunden, Variante gewählt:', this.#variantSelected);
    }

    // Fallback: Prüfe ob variant-picker existiert und vollständig ist
    if (!this.#variantSelected) {
      const variantPicker = document.querySelector('variant-picker-component');
      if (variantPicker) {
        // Prüfe ob alle required Optionen ausgewählt sind
        const selects = variantPicker.querySelectorAll('select');
        if (selects.length > 0) {
          this.#variantSelected = Array.from(selects).every(select => 
            select instanceof HTMLSelectElement && select.value !== ''
          );
          console.log('Variant Picker gefunden, alle Optionen gewählt:', this.#variantSelected);
        }
      }
    }

    // Fallback: Prüfe hidden input mit variant ID
    if (!this.#variantSelected) {
      const variantInput = document.querySelector('input[name="id"]');
      if (variantInput instanceof HTMLInputElement && variantInput.value) {
        this.#variantSelected = true;
        console.log('Variante via hidden input gefunden:', variantInput.value);
      }
    }
  }

  /**
   * Behandelt Varianten-Auswahl Events
   * @param {Event} event
   */
  #handleVariantSelected = (event) => {
    const customEvent = /** @type {CustomEvent} */ (event);
    
    // Prüfe ob die Variante vollständig ausgewählt ist
    if (customEvent.detail && customEvent.detail.resource && customEvent.detail.resource.id) {
      this.#variantSelected = true;
      this.#updateButtonStates();
    }
  };

  /**
   * Behandelt Varianten-Update Events
   * @param {Event} event
   */
  #handleVariantUpdate = (event) => {
    const customEvent = /** @type {CustomEvent} */ (event);
    
    if (customEvent.detail && customEvent.detail.resource && customEvent.detail.resource.id) {
      this.#variantSelected = true;
      this.#updateButtonStates();
    }
  };

  /**
   * Behandelt "Ohne Etikett" Checkbox Änderungen (Theme Event)
   * @param {Event} event
   */
  #handleNoLabelCheckboxChange = (event) => {
    const customEvent = /** @type {CustomEvent<{checked: boolean}>} */ (event);
    
    if (customEvent.detail && typeof customEvent.detail.checked === 'boolean') {
      this.#noLabelChecked = customEvent.detail.checked;
      console.log('Checkbox via Theme Event:', this.#noLabelChecked);
      this.#updateButtonStates();
    }
  };

  /**
   * Behandelt direkte Checkbox Änderungen
   * @param {Event} event
   */
  #handleCheckboxDirectChange = (event) => {
    if (event.target instanceof HTMLInputElement) {
      this.#noLabelChecked = event.target.checked;
      console.log('Checkbox direkt geändert:', this.#noLabelChecked);
      this.#updateButtonStates();
    }
  };

  /**
   * Behandelt FPD modalDesignerDone Event (Design wurde fertiggestellt)
   * Dies ist das primäre Event wenn User auf "Done" klickt
   * @param {Event} event
   */
  #handleFPDDesignCompleted = (event) => {
    console.log('FPD Design Completed (modalDesignerDone):', event);
    this.#fpdDesignCompleted = true;
    this.#updateButtonStates();
  };

  /**
   * Behandelt FPD Cart Changed Event (Design wurde zum Cart hinzugefügt)
   * Backup Event falls modalDesignerDone nicht gefeuert wird
   * @param {Event} event
   */
  #handleFPDCartChanged = (event) => {
    console.log('FPD Cart Changed Event:', event);
    this.#fpdDesignCompleted = true;
    this.#updateButtonStates();
  };

  /**
   * Aktualisiert alle Button-Zustände basierend auf dem aktuellen State
   */
  #updateButtonStates() {
    this.#updateFPDButton();
    this.#updateAddToCartButtons();
  }

  /**
   * Aktualisiert den FPD "Jetzt Etikett gestalten" Button
   * Aktiv nur wenn Variante vollständig ausgewählt
   */
  #updateFPDButton() {
    const fpdButton = document.getElementById('fpd-customize-button');
    
    if (fpdButton instanceof HTMLButtonElement) {
      if (this.#variantSelected) {
        fpdButton.disabled = false;
        fpdButton.style.opacity = '1';
        fpdButton.style.cursor = 'pointer';
        fpdButton.title = '';
      } else {
        fpdButton.disabled = true;
        fpdButton.style.opacity = '0.5';
        fpdButton.style.cursor = 'not-allowed';
        fpdButton.title = 'Bitte wählen Sie zuerst eine vollständige Variante aus';
      }
    }
  }

  /**
   * Aktualisiert Add-to-Cart und Payment Buttons
   * Aktiv nur wenn: FPD Design erstellt ODER "Ohne Etikett" aktiviert
   */
  #updateAddToCartButtons() {
    // Add-to-Cart Button
    const addToCartButton = document.querySelector('.add-to-cart-button');
    
    // Accelerated Checkout Buttons (PayPal, etc.)
    const acceleratedCheckout = document.querySelector('.shopify-payment-button');

    const canProceed = this.#fpdDesignCompleted || this.#noLabelChecked;

    // Add-to-Cart Button
    if (addToCartButton instanceof HTMLButtonElement) {
      if (canProceed) {
        addToCartButton.disabled = false;
        addToCartButton.style.opacity = '1';
        addToCartButton.style.cursor = 'pointer';
        addToCartButton.title = '';
      } else {
        addToCartButton.disabled = true;
        addToCartButton.style.opacity = '0.5';
        addToCartButton.style.cursor = 'not-allowed';
        addToCartButton.title = 'Bitte gestalten Sie ein Etikett oder wählen Sie "Ohne Etikett"';
      }
    }

    // Payment Buttons
    if (acceleratedCheckout instanceof HTMLElement) {
      if (canProceed) {
        acceleratedCheckout.style.opacity = '1';
        acceleratedCheckout.style.pointerEvents = 'auto';
        
        // Entferne disabled von allen Buttons darin
        const buttons = acceleratedCheckout.querySelectorAll('button');
        buttons.forEach(btn => {
          if (btn instanceof HTMLButtonElement) {
            btn.disabled = false;
          }
        });
      } else {
        acceleratedCheckout.style.opacity = '0.5';
        acceleratedCheckout.style.pointerEvents = 'none';
        acceleratedCheckout.title = 'Bitte gestalten Sie ein Etikett oder wählen Sie "Ohne Etikett"';
        
        // Disable alle Buttons darin
        const buttons = acceleratedCheckout.querySelectorAll('button');
        buttons.forEach(btn => {
          if (btn instanceof HTMLButtonElement) {
            btn.disabled = true;
          }
        });
      }
    }
  }

  /**
   * Gibt den aktuellen State zurück (für Debugging)
   * @returns {{variantSelected: boolean, fpdDesignCompleted: boolean, noLabelChecked: boolean}}
   */
  getState() {
    const state = {
      variantSelected: this.#variantSelected,
      fpdDesignCompleted: this.#fpdDesignCompleted,
      noLabelChecked: this.#noLabelChecked,
      canProceedToCart: this.#fpdDesignCompleted || this.#noLabelChecked,
    };
    console.log('Current FPD State:', state);
    return state;
  }

  /**
   * Setzt den State manuell (für Debugging/Testing)
   * @param {{variantSelected?: boolean, fpdDesignCompleted?: boolean, noLabelChecked?: boolean}} newState
   */
  setState(newState) {
    if (typeof newState.variantSelected === 'boolean') {
      this.#variantSelected = newState.variantSelected;
    }
    if (typeof newState.fpdDesignCompleted === 'boolean') {
      this.#fpdDesignCompleted = newState.fpdDesignCompleted;
    }
    if (typeof newState.noLabelChecked === 'boolean') {
      this.#noLabelChecked = newState.noLabelChecked;
    }
    console.log('State manually updated:', this.getState());
    this.#updateButtonStates();
  }
}

if (!customElements.get('fpd-product-state-component')) {
  customElements.define('fpd-product-state-component', FPDProductStateComponent);
}
